import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import {
  api,
  OpsEvent,
  OpsEventTypeMeta,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const DAY_OPTIONS = [
  { id: 7, label: "7 dni" },
  { id: 14, label: "14 dni" },
  { id: 30, label: "30 dni" },
] as const;

const INTENT_OPTIONS = [
  { id: "", label: "(brak)" },
  { id: "reach", label: "Zasięg" },
  { id: "edu", label: "Edukacja" },
  { id: "mofu", label: "MOFU" },
  { id: "sales", label: "Sprzedaż" },
  { id: "proof", label: "Proof" },
  { id: "other", label: "Inne" },
] as const;

const SOURCE_LABEL: Record<string, string> = {
  manual: "ręczne",
  auto_landing_deploy: "auto deploy",
  auto_landing_beacon: "auto beacon",
  auto_agent: "agent",
};

function todayWarsaw(): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Warsaw",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function typeLabel(types: OpsEventTypeMeta[], id: string): string {
  return types.find((t) => t.id === id)?.label || id;
}

function sourceVariant(source: string): "secondary" | "brand" | "outline" | "warning" {
  if (source === "manual") return "secondary";
  if (source.startsWith("auto_landing")) return "brand";
  if (source === "auto_agent") return "warning";
  return "outline";
}

function groupByDate(items: OpsEvent[]): { date: string; events: OpsEvent[] }[] {
  const map = new Map<string, OpsEvent[]>();
  for (const ev of items) {
    const list = map.get(ev.event_date) || [];
    list.push(ev);
    map.set(ev.event_date, list);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, events]) => ({ date, events }));
}

export default function OpsEvents() {
  const qc = useQueryClient();
  const [days, setDays] = useState<number>(14);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState("");
  const [editQty, setEditQty] = useState(1);

  const [formDate, setFormDate] = useState(todayWarsaw());
  const [formType, setFormType] = useState("reel");
  const [formQty, setFormQty] = useState(1);
  const [formTitle, setFormTitle] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formIntent, setFormIntent] = useState("");
  const [formFormats, setFormFormats] = useState("");
  const [formPlatform, setFormPlatform] = useState("IG");
  const [formError, setFormError] = useState("");

  const typesQ = useQuery({
    queryKey: ["ops-event-types"],
    queryFn: api.opsEventTypes,
  });
  const listQ = useQuery({
    queryKey: ["ops-events", days],
    queryFn: () => api.opsEventsList({ days }),
  });

  const types = typesQ.data?.types ?? [];
  const selectedType = types.find((t) => t.id === formType);
  const needsIntent = selectedType?.fields.includes("intent");
  const needsFormats = selectedType?.fields.includes("formats");
  const needsPlatform = selectedType?.fields.includes("platform");

  const grouped = useMemo(
    () => groupByDate(listQ.data?.items ?? []),
    [listQ.data?.items],
  );

  const createMut = useMutation({
    mutationFn: api.opsEventCreate,
    onSuccess: () => {
      setFormNotes("");
      setFormTitle("");
      setFormQty(1);
      setFormFormats("");
      setFormError("");
      qc.invalidateQueries({ queryKey: ["ops-events"] });
    },
    onError: (err: Error) => setFormError(err.message || "Błąd zapisu"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, notes, qty }: { id: string; notes: string; qty: number }) =>
      api.opsEventUpdate(id, { notes, qty }),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["ops-events"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: api.opsEventDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-events"] }),
  });

  function submitForm(e: FormEvent) {
    e.preventDefault();
    const attrs: Record<string, unknown> = {};
    if (needsIntent && formIntent) attrs.intent = formIntent;
    if (needsFormats && formFormats.trim()) {
      attrs.formats = formFormats
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (needsPlatform && formPlatform.trim()) attrs.platform = formPlatform.trim();
    createMut.mutate({
      event_date: formDate,
      event_type: formType,
      title: formTitle.trim(),
      notes: formNotes,
      qty: formQty,
      attrs,
      source: "manual",
      created_by: "dashboard_user",
    });
  }

  function startEdit(ev: OpsEvent) {
    setEditingId(ev.id);
    setEditNotes(ev.notes || "");
    setEditQty(ev.qty || 1);
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Dziennik działań (rolek, relacji, ads, landingów). Wpisy auto z deployu landingu
        i pierwszego beaconu nowej wersji pojawiają się same. Treści Kasi uzupełniacie ręcznie.
      </p>

      <div className="flex flex-wrap gap-2">
        {DAY_OPTIONS.map((o) => (
          <Button
            key={o.id}
            size="sm"
            variant={days === o.id ? "default" : "outline"}
            onClick={() => setDays(o.id)}
          >
            {o.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dodaj zdarzenie</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitForm} className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Data</span>
              <Input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                required
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Typ</span>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background/60 px-3 text-sm"
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Ilość</span>
              <Input
                type="number"
                min={0}
                value={formQty}
                onChange={(e) => setFormQty(Number(e.target.value) || 0)}
              />
            </label>
            {needsIntent ? (
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Intencja</span>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background/60 px-3 text-sm"
                  value={formIntent}
                  onChange={(e) => setFormIntent(e.target.value)}
                >
                  {INTENT_OPTIONS.map((o) => (
                    <option key={o.id || "none"} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {needsFormats ? (
              <label className="grid gap-1 text-sm md:col-span-2">
                <span className="text-muted-foreground">
                  Formaty (po przecinku, np. talking-head, tekst-na-ekranie)
                </span>
                <Input
                  value={formFormats}
                  onChange={(e) => setFormFormats(e.target.value)}
                  placeholder="talking-head, tekst-na-ekranie"
                />
              </label>
            ) : null}
            {needsPlatform ? (
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Platforma</span>
                <Input
                  value={formPlatform}
                  onChange={(e) => setFormPlatform(e.target.value)}
                  placeholder="IG / TT / YT"
                />
              </label>
            ) : null}
            <label className="grid gap-1 text-sm md:col-span-2 lg:col-span-3">
              <span className="text-muted-foreground">Tytuł (opcjonalnie)</span>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="np. 2 rolki MOFU o granicach"
              />
            </label>
            <label className="grid gap-1 text-sm md:col-span-2 lg:col-span-3">
              <span className="text-muted-foreground">Notatki</span>
              <textarea
                className="min-h-[72px] w-full rounded-md border border-input bg-background/60 px-3 py-2 text-sm"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Kontekst do późniejszej analizy (CTA, temat, co się zmieniło…)"
              />
            </label>
            <div className="flex items-center gap-3 md:col-span-2 lg:col-span-3">
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? "Zapis…" : "Dodaj"}
              </Button>
              {formError ? (
                <span className="text-sm text-destructive">{formError}</span>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>

      {listQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : listQ.isError ? (
        <p className="text-sm text-destructive">
          Nie udało się wczytać rejestru: {(listQ.error as Error)?.message}
        </p>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Brak zdarzeń w wybranym oknie. Dodaj pierwsze albo poczekaj na auto-wpis z landingu.
          </CardContent>
        </Card>
      ) : (
        grouped.map(({ date, events }) => (
          <Card key={date}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{date}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className={cn(
                    "rounded-md border border-border/70 p-3",
                    ev.hidden && "opacity-50",
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {typeLabel(types, ev.event_type)}
                          {ev.qty > 1 ? ` ×${ev.qty}` : ""}
                        </span>
                        <Badge variant={sourceVariant(ev.source)}>
                          {SOURCE_LABEL[ev.source] || ev.source}
                        </Badge>
                        {typeof ev.attrs?.intent === "string" && ev.attrs.intent ? (
                          <Badge variant="outline">{String(ev.attrs.intent)}</Badge>
                        ) : null}
                      </div>
                      <p className="text-sm">{ev.title}</p>
                      {editingId === ev.id ? (
                        <div className="mt-2 grid max-w-xl gap-2">
                          <Input
                            type="number"
                            min={0}
                            value={editQty}
                            onChange={(e) => setEditQty(Number(e.target.value) || 0)}
                          />
                          <textarea
                            className="min-h-[64px] w-full rounded-md border border-input bg-background/60 px-3 py-2 text-sm"
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                updateMut.mutate({
                                  id: ev.id,
                                  notes: editNotes,
                                  qty: editQty,
                                })
                              }
                            >
                              Zapisz
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingId(null)}
                            >
                              Anuluj
                            </Button>
                          </div>
                        </div>
                      ) : ev.notes ? (
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {ev.notes}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Bez notatki</p>
                      )}
                      {ev.attrs?.page_id || ev.attrs?.experiment_version ? (
                        <p className="font-mono text-xs text-muted-foreground">
                          {String(ev.attrs.page_id || "")}
                          {ev.attrs.experiment_version
                            ? ` · ${String(ev.attrs.experiment_version)}`
                            : ""}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => startEdit(ev)}>
                        Edytuj
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (ev.source === "manual") {
                            if (window.confirm("Usunąć to zdarzenie?")) {
                              deleteMut.mutate(ev.id);
                            }
                          } else if (window.confirm("Ukryć wpis auto?")) {
                            deleteMut.mutate(ev.id);
                          }
                        }}
                      >
                        {ev.source === "manual" ? "Usuń" : "Ukryj"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
